/* Consent persistence (per-tenant, localStorage).
 *
 * Plan §"Cookie Consent & Session Preference / Consent storage/audit":
 *   - persist consent version, timestamp, and accepted categories,
 *   - keep history append-only so revocations are auditable,
 *   - latest decision is the effective state.
 */

import type { ConsentDecision } from './consentTypes.js';
import { CONSENT_VERSION } from '../../shared/consent/constants.js';
import {
  clearTenantConsent,
  GLOBAL_ACCEPT_ALL_KEY,
  readEffectiveConsent,
  writeConsentDecision,
} from './consentPolicy.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';

const AUDIT_KEY_PREFIX = 'customer:consent:audit:';
export const GLOBAL_AUDIT_KEY = 'customer:consent:audit:global';
const AUDIT_MAX_ENTRIES = 50;

export interface ConsentStorageOptions {
  loyaltyProfilingAvailable?: boolean;
}

function auditKey(tenantCode: string): string {
  return `${AUDIT_KEY_PREFIX}${resolveConsentStorageTenantCode(tenantCode)}`;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getDefaultStorage(): StorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readConsent(
  tenantCode: string,
  storage: StorageLike | null = getDefaultStorage(),
  options?: ConsentStorageOptions
): ConsentDecision | null {
  return readEffectiveConsent({
    tenantCode,
    consentVersion: CONSENT_VERSION,
    loyaltyProfilingAvailable: options?.loyaltyProfilingAvailable ?? false,
    storage,
  });
}

export function writeConsent(
  tenantCode: string,
  decision: ConsentDecision,
  storage: StorageLike | null = getDefaultStorage(),
  options?: ConsentStorageOptions
): void {
  if (storage === null) {
    return;
  }
  const loyaltyProfilingAvailable = options?.loyaltyProfilingAvailable ?? false;
  const hadGlobal = storage.getItem(GLOBAL_ACCEPT_ALL_KEY) !== null;
  writeConsentDecision({
    tenantCode,
    decision,
    loyaltyProfilingAvailable,
    storage,
  });
  appendAudit(tenantCode, decision, storage);
  const hasGlobal = storage.getItem(GLOBAL_ACCEPT_ALL_KEY) !== null;
  if (hasGlobal || hadGlobal) {
    appendGlobalAudit(decision, storage);
  }
}

function appendAudit(
  tenantCode: string,
  decision: ConsentDecision,
  storage: StorageLike
): void {
  let history: ConsentDecision[] = [];
  const raw = storage.getItem(auditKey(tenantCode));
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw) as ConsentDecision[];
      if (Array.isArray(parsed)) {
        history = parsed;
      }
    } catch {
      history = [];
    }
  }
  history.push(decision);
  if (history.length > AUDIT_MAX_ENTRIES) {
    history = history.slice(-AUDIT_MAX_ENTRIES);
  }
  storage.setItem(auditKey(tenantCode), JSON.stringify(history));
}

function appendGlobalAudit(decision: ConsentDecision, storage: StorageLike): void {
  let history: ConsentDecision[] = [];
  const raw = storage.getItem(GLOBAL_AUDIT_KEY);
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw) as ConsentDecision[];
      if (Array.isArray(parsed)) {
        history = parsed;
      }
    } catch {
      history = [];
    }
  }
  history.push(decision);
  if (history.length > AUDIT_MAX_ENTRIES) {
    history = history.slice(-AUDIT_MAX_ENTRIES);
  }
  storage.setItem(GLOBAL_AUDIT_KEY, JSON.stringify(history));
}

export function clearConsent(
  tenantCode: string,
  storage: StorageLike | null = getDefaultStorage(),
  options?: ConsentStorageOptions
): void {
  if (storage === null) {
    return;
  }
  const loyaltyProfilingAvailable = options?.loyaltyProfilingAvailable ?? false;
  const hadGlobal = storage.getItem(GLOBAL_ACCEPT_ALL_KEY) !== null;
  const prior = readEffectiveConsent({
    tenantCode,
    consentVersion: CONSENT_VERSION,
    loyaltyProfilingAvailable,
    storage,
  });
  clearTenantConsent(tenantCode, storage);
  if (prior !== null || hadGlobal) {
    appendGlobalAudit(prior ?? {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      profiling: false,
      version: CONSENT_VERSION,
      decidedAtMs: Date.now(),
    }, storage);
  }
}
